<template>
  <div class="ce">
    <!-- Top bar -->
    <div class="ce-topbar">
      <div class="ce-topbar__left">
        <span class="ce-layout-chip">
          <i class="material-icons" style="font-size:14px;">dashboard</i>
          {{ layoutMeta.name }}
        </span>
        <button type="button" class="ce-change-btn" @click="$emit('change-layout')">
          <i class="material-icons" style="font-size:14px;">swap_horiz</i>
          Change Layout
        </button>
      </div>
      <div class="ce-topbar__right">
        <span class="ce-panel-label">EDIT</span>
        <span class="ce-panel-label">PREVIEW</span>
      </div>
    </div>

    <!-- Split pane -->
    <div class="ce-split">
      <!-- Left: edit form -->
      <div class="ce-edit">
        <div class="ce-form">

          <!-- Clean layout -->
          <template v-if="layout === 'clean'">
            <QuillEditor
              label="Body"
              :value="content.body"
              @input="updateField('body', $event)"
            />
            <ButtonEditor
              :value="content.button"
              @input="updateField('button', $event)"
            />
          </template>

          <!-- Hero layout -->
          <template v-if="layout === 'hero'">
            <ImageUploader
              label="Hero Image"
              :value="content.hero_image"
              :on-upload="onUpload"
              @input="updateField('hero_image', $event)"
            />
            <QuillEditor
              label="Body"
              :value="content.body"
              @input="updateField('body', $event)"
            />
            <ButtonEditor
              :value="content.button"
              @input="updateField('button', $event)"
            />
          </template>

          <!-- Event layout -->
          <template v-if="layout === 'event'">
            <ImageUploader
              label="Hero Image"
              :value="content.hero_image"
              :on-upload="onUpload"
              @input="updateField('hero_image', $event)"
            />
            <div class="ce-field-group">
              <div class="ce-field">
                <label class="ce-label">Event Name</label>
                <input
                  class="ce-input"
                  :value="content.event_name"
                  @input="updateField('event_name', $event.target.value)"
                  placeholder="e.g. Open House 2026"
                />
              </div>
              <div class="ce-field-row">
                <div class="ce-field ce-field--half">
                  <label class="ce-label">Date</label>
                  <input
                    class="ce-input"
                    :value="content.event_date"
                    @input="updateField('event_date', $event.target.value)"
                    placeholder="e.g. March 15, 2026"
                  />
                </div>
                <div class="ce-field ce-field--half">
                  <label class="ce-label">Time</label>
                  <input
                    class="ce-input"
                    :value="content.event_time"
                    @input="updateField('event_time', $event.target.value)"
                    placeholder="e.g. 2:00 PM - 4:00 PM"
                  />
                </div>
              </div>
              <div class="ce-field">
                <label class="ce-label">Location</label>
                <input
                  class="ce-input"
                  :value="content.event_location"
                  @input="updateField('event_location', $event.target.value)"
                  placeholder="e.g. Main Campus"
                />
              </div>
            </div>
            <QuillEditor
              label="Body"
              :value="content.body"
              @input="updateField('body', $event)"
            />
            <ButtonEditor
              :value="content.button"
              @input="updateField('button', $event)"
            />
          </template>

          <!-- Newsletter layout -->
          <template v-if="layout === 'newsletter'">
            <div class="ce-field" style="margin-bottom:16px;">
              <label class="ce-label">Newsletter Heading</label>
              <input
                class="ce-input"
                :value="content.heading"
                @input="updateField('heading', $event.target.value)"
                placeholder="e.g. Monthly Newsletter"
              />
            </div>

            <div
              v-for="(section, idx) in content.sections"
              :key="idx"
              class="ce-section"
            >
              <div class="ce-section-header">
                <span class="ce-section-num">Section {{ idx + 1 }}</span>
                <button
                  v-if="content.sections.length > 1"
                  type="button"
                  class="ce-section-remove"
                  @click="removeSection(idx)"
                >
                  <i class="material-icons" style="font-size:16px;">close</i>
                </button>
              </div>
              <ImageUploader
                label="Section Image (optional)"
                :value="section.image"
                :on-upload="onUpload"
                @input="updateSectionField(idx, 'image', $event)"
              />
              <div class="ce-field" style="margin-bottom:8px;">
                <label class="ce-label">Section Heading</label>
                <input
                  class="ce-input"
                  :value="section.heading"
                  @input="updateSectionField(idx, 'heading', $event.target.value)"
                  placeholder="Section title"
                />
              </div>
              <QuillEditor
                label="Section Body"
                :value="section.body"
                @input="updateSectionField(idx, 'body', $event)"
              />
            </div>

            <button
              v-if="content.sections && content.sections.length < 4"
              type="button"
              class="ce-add-section"
              @click="addSection"
            >
              <i class="material-icons" style="font-size:16px;">add</i>
              Add Section
            </button>
          </template>

          <!-- Welcome layout -->
          <template v-if="layout === 'welcome'">
            <div class="ce-field" style="margin-bottom:16px;">
              <label class="ce-label">Heading</label>
              <input
                class="ce-input"
                :value="content.heading"
                @input="updateField('heading', $event.target.value)"
                placeholder="e.g. Welcome!"
              />
            </div>
            <QuillEditor
              label="Body"
              :value="content.body"
              @input="updateField('body', $event)"
            />

            <!-- Highlights list -->
            <div class="ce-highlights">
              <label class="ce-label">Key Highlights</label>
              <div
                v-for="(hl, idx) in content.highlights"
                :key="idx"
                class="ce-highlight-row"
              >
                <input
                  class="ce-input ce-input--flex"
                  :value="hl"
                  @input="updateHighlight(idx, $event.target.value)"
                  placeholder="e.g. Flexible schedules"
                />
                <button
                  v-if="content.highlights.length > 1"
                  type="button"
                  class="ce-highlight-remove"
                  @click="removeHighlight(idx)"
                >
                  <i class="material-icons" style="font-size:16px;">close</i>
                </button>
              </div>
              <button
                v-if="content.highlights && content.highlights.length < 8"
                type="button"
                class="ce-add-highlight"
                @click="addHighlight"
              >
                <i class="material-icons" style="font-size:14px;">add</i> Add highlight
              </button>
            </div>

            <ButtonEditor
              :value="content.button"
              @input="updateField('button', $event)"
            />
          </template>

        </div>
      </div>

      <!-- Right: live preview -->
      <div class="ce-preview">
        <div class="ce-preview-frame">
          <div class="ce-preview-chrome">
            <span class="ce-preview-dot"></span>
            <span class="ce-preview-dot"></span>
            <span class="ce-preview-dot"></span>
          </div>
          <div class="ce-preview-body" v-html="previewHtml"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { LAYOUTS, renderLayout } from '../../helpers/email-layouts';
import QuillEditor from './QuillEditor.vue';
import ImageUploader from './ImageUploader.vue';
import ButtonEditor from './ButtonEditor.vue';

export default {
  name: 'ContentEditor',
  components: { QuillEditor, ImageUploader, ButtonEditor },

  props: {
    layout: { type: String, required: true },
    content: { type: Object, required: true },
    onUpload: { type: Function, required: true },
  },

  computed: {
    layoutMeta: function () {
      return LAYOUTS.find(function (l) { return l.id === this.layout; }.bind(this)) || { name: '', description: '' };
    },

    previewHtml: function () {
      return renderLayout(this.layout, this.content);
    },
  },

  methods: {
    updateField: function (field, value) {
      var updated = Object.assign({}, this.content);
      updated[field] = value;
      this.$emit('update:content', updated);
    },

    // Newsletter section helpers
    updateSectionField: function (idx, field, value) {
      var sections = this.content.sections.map(function (s) { return Object.assign({}, s); });
      sections[idx][field] = value;
      this.updateField('sections', sections);
    },

    addSection: function () {
      var sections = this.content.sections.slice();
      sections.push({ image: '', heading: '', body: '<p>Write section content here.</p>' });
      this.updateField('sections', sections);
    },

    removeSection: function (idx) {
      var sections = this.content.sections.slice();
      sections.splice(idx, 1);
      this.updateField('sections', sections);
    },

    // Welcome highlights helpers
    updateHighlight: function (idx, value) {
      var highlights = this.content.highlights.slice();
      highlights[idx] = value;
      this.updateField('highlights', highlights);
    },

    addHighlight: function () {
      var highlights = this.content.highlights.slice();
      highlights.push('');
      this.updateField('highlights', highlights);
    },

    removeHighlight: function (idx) {
      var highlights = this.content.highlights.slice();
      highlights.splice(idx, 1);
      this.updateField('highlights', highlights);
    },
  },
};
</script>

<style scoped>
.ce {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.ce-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 44px;
  background: #fff;
  border-bottom: 1px solid #e2e6ea;
  flex-shrink: 0;
}
.ce-topbar__left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ce-topbar__right {
  display: flex;
  align-items: center;
  gap: 0;
}
.ce-layout-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  background: #e8f0fe;
  color: var(--primary-color, #3b86f5);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}
.ce-change-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border: 1px solid #dadce0;
  border-radius: 12px;
  background: #fff;
  font-size: 12px;
  color: #5f6368;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.ce-change-btn:hover {
  background: #f4f3ed;
  border-color: #c4c8d0;
}
.ce-panel-label {
  font-size: 10px;
  font-weight: 600;
  color: #9aa0a6;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  width: 50%;
  text-align: center;
  display: none;
}

.ce-split {
  flex: 1;
  min-height: 0;
  display: flex;
}

.ce-edit {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 20px 24px;
  background: #f4f3ed;
  border-right: 1px solid #e2e6ea;
}
.ce-form {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e2e6ea;
  padding: 24px 28px;
}

.ce-preview {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 20px 24px;
  background: #eae9e3;
}
.ce-preview-frame {
  border-radius: 8px;
  border: 1px solid #dadce0;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
}
.ce-preview-chrome {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  background: #f4f3ed;
  border-bottom: 1px solid #e2e6ea;
}
.ce-preview-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #dadce0;
}
.ce-preview-body {
  padding: 24px 28px;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  color: #181818;
  word-wrap: break-word;
}
.ce-preview-body img {
  max-width: 100%;
  height: auto;
}

.ce-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}
.ce-field--half {
  flex: 1;
  min-width: 0;
}
.ce-field-row {
  display: flex;
  gap: 12px;
  margin-bottom: 10px;
}
.ce-field-group {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #e2e6ea;
  border-radius: 8px;
  background: #fafbfc;
}
.ce-label {
  font-size: 12px;
  font-weight: 600;
  color: #5f6368;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.ce-input {
  height: 36px;
  padding: 0 10px;
  border: 1px solid #dadce0;
  border-radius: 6px;
  font-size: 14px;
  color: #181818;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
}
.ce-input:focus {
  border-color: var(--primary-color, #3b86f5);
}
.ce-input--flex {
  flex: 1;
  min-width: 0;
}

.ce-section {
  margin-bottom: 20px;
  padding: 16px;
  border: 1px solid #e2e6ea;
  border-radius: 8px;
  background: #fafbfc;
}
.ce-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.ce-section-num {
  font-size: 13px;
  font-weight: 600;
  color: #5f6368;
}
.ce-section-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #9aa0a6;
  cursor: pointer;
}
.ce-section-remove:hover {
  background: #fce8e6;
  color: #d93025;
}

.ce-add-section {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px dashed #dadce0;
  border-radius: 6px;
  background: #fff;
  color: var(--primary-color, #3b86f5);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  margin-top: 4px;
}
.ce-add-section:hover {
  background: #e8f0fe;
  border-color: var(--primary-color, #3b86f5);
}

.ce-highlights {
  margin-bottom: 16px;
}
.ce-highlight-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}
.ce-highlight-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #9aa0a6;
  cursor: pointer;
  flex-shrink: 0;
}
.ce-highlight-remove:hover {
  background: #fce8e6;
  color: #d93025;
}
.ce-add-highlight {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 4px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--primary-color, #3b86f5);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}
.ce-add-highlight:hover {
  background: #e8f0fe;
}
</style>
